import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, ExternalLink, Loader2, Link2 } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const fmt = (v) => (v === null || v === undefined ? "N/D" : Number(v).toLocaleString("es-ES"));

const ChartCard = ({ title, children, testId }) => (
  <div className="rounded-lg border border-slate-200 p-3" data-testid={testId}>
    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">{title}</div>
    <div className="h-44">{children}</div>
  </div>
);

const tinyTick = { fontSize: 10, fill: "#64748b" };

function RffmCharts({ seasons }) {
  const rows = [...seasons]
    .sort((a, b) => String(a.season_label).localeCompare(String(b.season_label)))
    .map((s) => ({
      temporada: (s.season_label || "").replace("-20", "/"),
      Minutos: s.minutes ?? 0,
      Convocado: s.convocations ?? 0,
      Titular: s.starts ?? 0,
      Jugados: s.matches_played ?? 0,
      Goles: s.goals ?? 0,
      Amarillas: s.yellow_cards ?? 0,
      Rojas: s.red_cards ?? 0,
    }));
  if (rows.length === 0) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="rffm-charts">
      <ChartCard title="Minutos por temporada" testId="rffm-chart-minutos">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 5, right: 5, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="temporada" tick={tinyTick} axisLine={false} tickLine={false} />
            <YAxis tick={tinyTick} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => Number(v).toLocaleString("es-ES")} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
            <Bar dataKey="Minutos" fill="#5B2383" radius={[4, 4, 0, 0]} maxBarSize={44} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
      <ChartCard title="Participación por temporada" testId="rffm-chart-participacion">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 5, right: 5, left: -22, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="temporada" tick={tinyTick} axisLine={false} tickLine={false} />
            <YAxis tick={tinyTick} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar dataKey="Convocado" fill="#c4b5fd" radius={[4, 4, 0, 0]} maxBarSize={22} />
            <Bar dataKey="Jugados" fill="#7c3aed" radius={[4, 4, 0, 0]} maxBarSize={22} />
            <Bar dataKey="Titular" fill="#3d1758" radius={[4, 4, 0, 0]} maxBarSize={22} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
      <ChartCard title="Goles y tarjetas" testId="rffm-chart-goles">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 5, right: 5, left: -22, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="temporada" tick={tinyTick} axisLine={false} tickLine={false} />
            <YAxis tick={tinyTick} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar dataKey="Goles" fill="#D9B300" radius={[4, 4, 0, 0]} maxBarSize={22} />
            <Bar dataKey="Amarillas" fill="#facc15" radius={[4, 4, 0, 0]} maxBarSize={22} />
            <Bar dataKey="Rojas" fill="#e11d48" radius={[4, 4, 0, 0]} maxBarSize={22} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

export default function PlayerRffmSection({ playerId }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const canManage = user?.role === "admin";

  const load = () => api.get(`/rffm/players/${playerId}/seasons`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [playerId]);

  const link = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      await api.post(`/rffm/players/${playerId}/link`, { rffm_player_code: code.trim() }, { timeout: 60000 });
      toast.success("Jugador vinculado a RFFM · histórico de 3 temporadas descargado");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const resync = async () => {
    setBusy(true);
    try {
      await api.post(`/rffm/players/${playerId}/sync`, {}, { timeout: 60000 });
      toast.success("Datos RFFM actualizados");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const seasons = (data.seasons || []).filter((s) => s.read_status !== "JUGADOR_NO_ENCONTRADO");

  if (!data.rffm_player_code) {
    return (
      <div className="space-y-3" data-testid="rffm-not-linked">
        <p className="text-sm text-slate-500">
          Este jugador aún no está vinculado a su ficha federativa RFFM.
          {data.rffm_link_status === "REQUIERE_VALIDACION" && (
            <span className="block mt-1 font-bold text-amber-700">Requiere validación: hay varias fichas posibles.</span>
          )}
        </p>
        {(data.rffm_link_candidates || []).map((c) => (
          <button key={c.rffm_player_code} onClick={() => setCode(c.rffm_player_code)}
            className="block text-left w-full p-2 rounded border border-amber-200 bg-amber-50 text-sm hover:bg-amber-100"
            data-testid={`rffm-candidate-${c.rffm_player_code}`}>
            {c.name} · código {c.rffm_player_code}
          </button>
        ))}
        {canManage && (
          <div className="flex gap-2">
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Código RFFM (nº de fichajugador)" className="max-w-xs" data-testid="rffm-link-code" />
            <Button onClick={link} disabled={busy || !code.trim()} className="bg-purple-600 hover:bg-purple-700 text-white font-bold" data-testid="rffm-link-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2 className="h-4 w-4 mr-1.5" /> Vincular</>}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="rffm-section-content">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-slate-500">
          Código RFFM <b className="text-slate-700">{data.rffm_player_code}</b>
          {data.rffm_last_sync && <> · Última sincronización: {new Date(data.rffm_last_sync).toLocaleString("es-ES")}</>}
        </div>
        <div className="flex gap-2">
          {data.rffm_profile_url && (
            <a href={data.rffm_profile_url} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" data-testid="rffm-profile-link">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Ficha RFFM
              </Button>
            </a>
          )}
          {canManage && (
            <Button variant="outline" size="sm" onClick={resync} disabled={busy}
              className="border-purple-300 text-purple-700 font-bold" data-testid="rffm-resync-btn">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Actualizar</>}
            </Button>
          )}
        </div>
      </div>
      {seasons.length === 0 ? (
        <p className="text-sm text-slate-400 italic">Sin datos RFFM en las últimas 3 temporadas.</p>
      ) : (
        <>
          <RffmCharts seasons={seasons} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {seasons.map((s) => (
            <div key={s.season_id} className="rounded-lg border border-slate-200 p-3" data-testid={`rffm-season-${s.season_id}`}>
              <div className="flex items-center justify-between">
                <div className="font-black text-purple-800" style={{ fontFamily: "Nunito" }}>{s.season_label}</div>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-sky-100 text-sky-800">Fuente: RFFM</span>
              </div>
              <div className="text-[11px] text-slate-500 truncate">{s.team_name || "—"}</div>
              <div className="text-[10px] text-slate-400 truncate mb-2">{s.competition || ""}{s.group_name ? ` · ${s.group_name}` : ""}</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <span className="text-slate-500">Convocado</span><span className="font-bold text-right">{fmt(s.convocations)}</span>
                <span className="text-slate-500">Titular</span><span className="font-bold text-right">{fmt(s.starts)}</span>
                <span className="text-slate-500">Suplente</span><span className="font-bold text-right">{fmt(s.substitute)}</span>
                <span className="text-slate-500">Jugados</span><span className="font-bold text-right">{fmt(s.matches_played)}</span>
                <span className="text-slate-500">Goles</span><span className="font-bold text-right">{fmt(s.goals)}</span>
                <span className="text-slate-500">Minutos</span><span className="font-bold text-right">{fmt(s.minutes)}</span>
                <span className="text-slate-500">Amarillas</span><span className="font-bold text-right">{fmt(s.yellow_cards)}</span>
                <span className="text-slate-500">Rojas</span><span className="font-bold text-right">{fmt(s.red_cards)}</span>
                {s.pct_participation != null && (<><span className="text-slate-500">% Participación</span><span className="font-bold text-right">{s.pct_participation}%</span></>)}
                {s.pct_starts != null && (<><span className="text-slate-500">% Titular</span><span className="font-bold text-right">{s.pct_starts}%</span></>)}
                {s.minutes_per_match != null && (<><span className="text-slate-500">Min/partido</span><span className="font-bold text-right">{s.minutes_per_match}</span></>)}
              </div>
              {s.url && (
                <a href={s.url} target="_blank" rel="noreferrer" className="text-[11px] text-purple-700 font-bold hover:underline block mt-2">
                  Ver en RFFM →
                </a>
              )}
            </div>
          ))}
          </div>
        </>
      )}
      <p className="text-[10px] text-slate-400">
        Datos oficiales públicos de RFFM (resumen por temporada). La temporada actual se gestiona con los datos propios de EF Concepción y nunca se sobrescribe.
      </p>
    </div>
  );
}
